import {
  type FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import store, { useAppSelector } from '@redux/store.ts';
import { AnimatePresence } from 'framer-motion';

import {
  useGetInitialRewardsMutation,
  useGetVipInfoQuery,
} from '@/api/queries/user';
import { IntroContext } from '@/components/Intro/context';
import { WelcomeLottery } from '@/components/Lottery/WelcomeLottery/WelcomeLottery';

import { type onBoardingStatus, onBoardStatusesOrder } from './model/Config';
import { FirstVipRegistrationPage } from './screens/FirstVipRegistrationPage/FirstVipRegistrationPage';

import 'swiper/swiper-bundle.css';

const Onboarding: FC = () => {
  useGetVipInfoQuery();
  const showOnboarding = useAppSelector((state) => state.user.showOnboarding);
  const [onBoardingStatus, setOnBoardingStatus] = useState<onBoardingStatus>();
  const { startOnboarding } = useContext(IntroContext);
  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const userBalance = useAppSelector((state) => state.user.user?.balance);
  const [getInitialRewards, { isLoading: initialRewardsLoading }] =
    useGetInitialRewardsMutation();

  const prevScreen = useRef<onBoardingStatus>();

  const handleStatusChange = (status: onBoardingStatus): void => {
    const state = store.getState();
    const availableRegistrationOffer =
      state.user.vip?.paidVIPCost.availableRegistrationOffer;

    if (status === 'firstVipRegistration') {
      if (availableRegistrationOffer) {
        setOnBoardingStatus('firstVipRegistration');
      } else {
        return handleStatusChange('completed');
      }
    }

    setOnBoardingStatus((prev) => {
      if (prev !== 'firstLogin' && status === 'completed') {
        startOnboarding('welcome');
      }

      return status;
    });
  };

  useLayoutEffect(() => {
    if (showOnboarding) {
      setOnBoardingStatus('firstLogin');
    }
  }, [showOnboarding]);

  useEffect(() => {
    if (!onBoardingStatus) return;
    prevScreen.current = onBoardingStatus;
    const cb = (): void => {
      const index = onBoardStatusesOrder.indexOf(onBoardingStatus);
      setOnBoardingStatus(onBoardStatusesOrder[index - 1]);
    };

    if (['completed', 'firstLogin'].includes(onBoardingStatus)) {
      Telegram.WebApp.BackButton.hide();

      return;
    }
    Telegram.WebApp.BackButton.show();
    Telegram.WebApp.BackButton.onClick(cb);

    return (): void => {
      Telegram.WebApp.BackButton.offClick(cb);
    };
  }, [onBoardingStatus]);

  useEffect(() => {
    if (
      !!isFirstVisit &&
      (userBalance?.cash ?? 0) === 0 &&
      onBoardingStatus &&
      onBoardingStatus !== 'completed' &&
      !initialRewardsLoading
    ) {
      getInitialRewards();
    }
  }, [isFirstVisit, userBalance, onBoardingStatus, initialRewardsLoading]);

  if (onBoardingStatus == 'completed') return null;

  return (
    <AnimatePresence mode="sync">
      {onBoardingStatus === 'firstVipRegistration' && (
        <FirstVipRegistrationPage
          handleStatusChange={handleStatusChange}
          key="first-vip"
        />
      )}
      {onBoardingStatus === 'firstLogin' && (
        <WelcomeLottery handleStatusChange={handleStatusChange} />
        // <TournamentWelcome
        //   handleStatusChange={handleStatusChange}
        //   prevScreen={prevScreen.current}
        //   key="onboarding"
        // />
      )}
    </AnimatePresence>
  );
};

export { Onboarding };
